/**
 * Utility functions for optimizing Cloudinary image URLs
 */

export type ImageOptimizationOptions = {
    width?: number;
    height?: number;
    quality?: 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | number;
    format?: 'auto' | 'webp' | 'avif' | 'jpg' | 'png';
    crop?: 'fill' | 'fit' | 'scale' | 'thumb';
    aspectRatio?: string; // e.g., '16:9', '1:1'
    gravity?: 'auto' | 'face' | 'center';
    fetchFormat?: 'auto';
    trim?: boolean; // strips surrounding uniform padding (e_trim) — e.g. transparent margin around a logo
};

/**
 * Checks if a URL is a Cloudinary URL
 */
const isCloudinaryUrl = (url: string): boolean => {
    try {
        const { hostname } = new URL(url.startsWith('//') ? `https:${url}` : url);
        return hostname === 'cloudinary.com' || hostname.endsWith('.cloudinary.com');
    } catch {
        return false;
    }
};

/**
 * Checks if a URL already has transformations applied
 * Version segments (e.g. v1546620922) are not transformations
 */
const hasTransformations = (url: string): boolean => {
    if (!url.includes('/image/upload/')) return false;
    const afterUpload = url.split('/image/upload/')[1];
    if (!afterUpload?.includes('/')) return false;
    const firstSegment = afterUpload.split('/')[0];
    // Version segments like "v1546620922" are not transformations
    if (/^v\d+$/.test(firstSegment)) return false;
    return true;
};

/**
 * Builds Cloudinary transformation string from options
 */
const buildTransformationString = (options: ImageOptimizationOptions): string => {
    const transformations: string[] = [];

    // Trim must run before sizing so downstream dimensions apply to the trimmed image
    if (options.trim) {
        transformations.push('e_trim');
    }

    if (options.width) {
        transformations.push(`w_${options.width}`);
    }

    if (options.height) {
        transformations.push(`h_${options.height}`);
    }

    if (options.aspectRatio) {
        transformations.push(`ar_${options.aspectRatio}`);
    }

    if (options.crop) {
        transformations.push(`c_${options.crop}`);
    }

    if (options.gravity) {
        transformations.push(`g_${options.gravity}`);
    }

    if (options.quality) {
        const qualityValue = typeof options.quality === 'number' ? options.quality : options.quality;
        transformations.push(`q_${qualityValue}`);
    }

    if (options.format) {
        transformations.push(`f_${options.format}`);
    }

    if (options.fetchFormat) {
        transformations.push(`fl_${options.fetchFormat}`);
    }

    return transformations.join(',');
};

/**
 * Width rungs the venue-photo proxy is asked for.
 *
 * The backend resolver caches Google media URLs per (photoName, width) and each distinct
 * width is its own Places call, so requests snap to a short ladder instead of passing
 * arbitrary preset widths through. Browsers downscale the overshoot invisibly.
 */
const VENUE_PHOTO_WIDTH_RUNGS = [400, 800, 1600] as const;

/**
 * Checks if a URL is the self-healing venue-photo proxy.
 *
 * Matched on pathname, not the raw string, so a query string echoing the path cannot
 * fake a match. Deliberately narrower than "any Bizly proxy": the sibling team-logo
 * proxy takes no width and must be left alone.
 */
const isVenuePhotoProxyUrl = (url: string): boolean => {
    try {
        const { pathname } = new URL(url.startsWith('//') ? `https:${url}` : url);
        return pathname.includes('/venue-photos/');
    } catch {
        return false;
    }
};

const snapToWidthRung = (width: number): number =>
    VENUE_PHOTO_WIDTH_RUNGS.find(rung => width <= rung) ?? VENUE_PHOTO_WIDTH_RUNGS[VENUE_PHOTO_WIDTH_RUNGS.length - 1];

/**
 * Applies a snapped `?w=` to a venue-photo proxy URL, replacing any width already there.
 */
const withVenuePhotoWidth = (url: string, width: number): string => {
    try {
        const parsed = new URL(url.startsWith('//') ? `https:${url}` : url);
        parsed.searchParams.set('w', String(snapToWidthRung(width)));
        return parsed.toString();
    } catch {
        return url;
    }
};

/**
 * Optimizes a Cloudinary image URL with transformations
 * If the URL is not a Cloudinary URL, returns it unchanged
 */
export const optimizeImageUrl = (url: string, options: ImageOptimizationOptions = {}): string => {
    if (!url || typeof url !== 'string') {
        return url || '';
    }

    // The venue-photo proxy resizes at the source (Google Places maxWidthPx, or a Cloudinary
    // transform on healed rows), so it takes a width param rather than a URL transformation.
    // Without this branch every preset was a silent no-op here and cards loaded full-size.
    if (isVenuePhotoProxyUrl(url)) {
        return options.width ? withVenuePhotoWidth(url, options.width) : url;
    }

    // Only optimize Cloudinary URLs
    if (!isCloudinaryUrl(url)) {
        return url;
    }

    // Check if URL has the expected Cloudinary structure
    if (!url.includes('/image/upload/')) {
        // URL might be a different Cloudinary format, return as-is
        return url;
    }

    const transformationString = buildTransformationString(options);

    // If no transformations to apply, return original
    if (!transformationString) {
        return url;
    }

    // If URL already has transformations, we need to insert new ones
    if (hasTransformations(url)) {
        try {
            const parts = url.split('/image/upload/');
            if (parts.length === 2) {
                const [base, rest] = parts;
                const pathParts = rest.split('/');
                const existingTransform = pathParts[0];
                const remainingPath = pathParts.slice(1).join('/');

                // Combine transformations if both exist
                const combinedTransform = existingTransform
                    ? `${existingTransform},${transformationString}`
                    : transformationString;

                return `${base}/image/upload/${combinedTransform}/${remainingPath}`;
            }
        } catch (error) {
            // If transformation fails, return original URL
            console.warn('Failed to optimize image URL:', error);
            return url;
        }
    }

    // Insert transformations into the URL
    try {
        return url.replace('/image/upload/', `/image/upload/${transformationString}/`);
    } catch (error) {
        // If transformation fails, return original URL
        console.warn('Failed to optimize image URL:', error);
        return url;
    }
};

/**
 * Preset optimization configurations for different use cases
 */
export const ImageOptimizationPresets = {
    /**
     * Small thumbnail (e.g., venue cards in lists)
     * ~300px width, auto quality, modern format
     */
    thumbnail: {
        width: 300,
        quality: 'auto:good' as const,
        format: 'auto' as const,
        crop: 'fill' as const,
        gravity: 'auto' as const,
    },

    /**
     * Medium size (e.g., venue tiles)
     * ~400-500px width, auto quality, modern format
     */
    medium: {
        width: 500,
        quality: 'auto:good' as const,
        format: 'auto' as const,
        crop: 'fill' as const,
        gravity: 'auto' as const,
    },

    /**
     * Large size (e.g., featured venues, proposals)
     * ~600-800px width, high quality, modern format
     */
    large: {
        width: 800,
        quality: 'auto:best' as const,
        format: 'auto' as const,
        crop: 'fill' as const,
        gravity: 'auto' as const,
    },

    /**
     * Full size (e.g., venue detail pages)
     * Original width, best quality, modern format
     */
    full: {
        quality: 'auto:best' as const,
        format: 'auto' as const,
    },

    /**
     * Square thumbnail (e.g., profile images, small cards)
     * 200x200px, auto quality, modern format
     */
    square: {
        width: 200,
        height: 200,
        quality: 'auto:good' as const,
        format: 'auto' as const,
        crop: 'fill' as const,
        gravity: 'auto' as const,
    },
};

/**
 * Generates responsive srcset for Cloudinary images
 */
export const generateResponsiveSrcSet = (
    baseUrl: string,
    sizes: number[],
    options: Omit<ImageOptimizationOptions, 'width'> = {}
): string => {
    if (!isCloudinaryUrl(baseUrl)) {
        return '';
    }

    return sizes
        .map(size => {
            const optimizedUrl = optimizeImageUrl(baseUrl, { ...options, width: size });
            return `${optimizedUrl} ${size}w`;
        })
        .join(', ');
};

/**
 * Whether an image that failed to load is worth retrying at its untransformed URL.
 *
 * True only when the optimized URL is a genuinely different resource that could fail on its
 * own — i.e. a Cloudinary transform. A widened venue-photo proxy URL asks the same backend
 * for the same photo at a smaller size, so a failure means the stored photo is dead, not the
 * width: retrying un-widened just burns a request against a keyless, rate-limited endpoint
 * and delays the venue-level `{venueId}-fallback` retry that actually heals the row.
 */
export const shouldRetryUnoptimized = (sourceUrl: string, optimizedUrl: string): boolean => {
    if (!sourceUrl || optimizedUrl === sourceUrl) {
        return false;
    }

    return !isVenuePhotoProxyUrl(sourceUrl);
};
